import { buildURL } from "app/client/lib/urlUtils";
import { getMainOrgUrl } from "app/client/models/gristUrlState";
import { BaseAPI } from "app/common/BaseAPI";
import { OAuthClient } from "app/common/LoginPageConfig";

/**
 * API for performing actions related to an OAuth interaction.
 */
export class OAuthAPI extends BaseAPI {
  /**
   * The URL prefix for the OAuth interaction (e.g. `https://login.getgrist.com.com/oauth/interaction/:cid`).
   */
  private _interactionUrl: string;

  constructor(private _cid: string, private _client: OAuthClient) {
    super();

    const baseUrl = getMainOrgUrl().replace(/\/$/, "");
    const prefix = `/oauth/interaction/${_cid}`;
    this._interactionUrl = baseUrl + prefix;
  }

  /**
   * Redirects to the login page (`/login`) to add a new account.
   *
   * Includes `cid` and `client_id` query parameters. On successful login,
   * the user will be redirected back to the OAuth interaction page with the
   * specified `cid`.
   */
  public redirectToLogin(): void {
    const url = buildURL("/login", {
      hash: null,
      searchParams: new URLSearchParams({
        cid: this._cid,
        client_id: this._client.id,
      }),
    });
    window.location.assign(url.href);
  }

  /**
   * Redirects to the select account page (`/oauth/interaction/:cid/select-account`).
   */
  public redirectToSelectAccount(): void {
    window.location.assign(`${this._interactionUrl}/select-account`);
  }

  /**
   * Returns the URL of the consent page (`/oauth/interaction/:cid/consent`).
   *
   * This URL is used to request the consent page for a user selected in the "select_account"
   * prompt. The caller must specify which user was selected in the `authuser` form data key,
   * which is the 0-based index of the selected user.
   *
   * If a user has already allowed access to the requested scopes, a request to this URL will
   * not retrieve the consent page, but will instead redirect to the OAuth client redirect URI
   * with an authorization code.
   */
  public getConsentUrl(): string {
    return `${this._interactionUrl}/consent`;
  }

  /**
   * Allows or denies the requested scopes on the consent form page (`/oauth/interaction/:cid/consent`).
   *
   * Returns a redirect URL that redirects to the OAuth client with an authorization code (if allowed)
   * or an error (if denied).
   */
  public consent(decision: "allow" | "deny"): Promise<{ redirectUrl: string }> {
    return this.requestJson(`${this._interactionUrl}/consent`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    });
  }
}
